import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BLOCK_TYPES,
  BLOCK_TYPE_LABEL,
  blankBlockData,
  parseBlock,
  summariseBlock,
  type DraftBlock,
} from './pageSchemaModel';
import type { BlockType } from './blocks';
import type { BlockSchedules, BlockWindow } from '../dataProvider';
import type { Catalog } from './useCatalog';
import { BlockConfigForm } from './BlockConfigForm';
import {
  SCHEDULE_COLOR,
  SCHEDULE_LABEL,
  blockScheduleState,
} from './blockSchedule';

/**
 * The drag-drop block COMPOSER (D-page-schema-editor-8a, 8d). A palette of the nine
 * block types to add from, a sortable list of the added blocks (drag to reorder,
 * via dnd-kit, plus accessible move up / down), per-block remove, and a collapsible
 * per-block config form. Each card is the minimal STRUCTURAL representation (type +
 * a resolved data summary + a validity chip) needed to arrange the page; this is
 * NOT the live preview (that is sub-surface 8c).
 *
 * SCHEDULING (8d): each card also shows a derived schedule chip (scheduled / live /
 * expired, off the injected clock) and, when expanded, the per-block visibility window
 * inputs. The window lives ALONGSIDE the block (a blockSchedules map keyed by block
 * id), NOT inside the strict data editor (data is the render-contract mirror).
 */

/** A datetime-local input value (the strict ISO is sliced to minutes for the field). */
const inputValue = (iso: string | null): string =>
  iso ? iso.slice(0, 16) : '';

const SortableBlockCard = ({
  block,
  index,
  total,
  expanded,
  onToggle,
  onMove,
  onRemove,
  onConfig,
  catalog,
  window,
  onWindow,
  now,
}: {
  block: DraftBlock;
  index: number;
  total: number;
  expanded: boolean;
  onToggle: () => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  onConfig: (data: Record<string, unknown>) => void;
  catalog: Catalog;
  window: BlockWindow | undefined;
  onWindow: (window: BlockWindow) => void;
  now: number;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: block.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const valid = parseBlock(block).success;
  const summary = summariseBlock(
    block,
    catalog.resolveStore,
    catalog.resolveCategory,
  );
  const scheduleState = blockScheduleState(window, now);
  const setBound = (key: keyof BlockWindow, value: string) =>
    onWindow({
      visibleFrom: window?.visibleFrom ?? null,
      visibleUntil: window?.visibleUntil ?? null,
      [key]: value === '' ? null : value,
    });

  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{ mb: 1 }}
      data-testid={`block-card-${block.id}`}
    >
      <CardContent sx={{ py: 1.5 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Button
            size="small"
            variant="text"
            sx={{ cursor: 'grab', minWidth: 0 }}
            data-testid={`block-drag-${block.id}`}
            {...attributes}
            {...listeners}
          >
            Drag
          </Button>
          <Typography variant="subtitle2" sx={{ minWidth: 120 }}>
            {BLOCK_TYPE_LABEL[block.type]}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flexGrow: 1 }}
            data-testid={`block-summary-${block.id}`}
          >
            {summary}
          </Typography>
          <Chip
            size="small"
            color={valid ? 'success' : 'warning'}
            variant="outlined"
            label={valid ? 'Ready' : 'Needs setup'}
            data-testid={`block-validity-${block.id}`}
          />
          <Chip
            size="small"
            color={SCHEDULE_COLOR[scheduleState]}
            variant="outlined"
            label={SCHEDULE_LABEL[scheduleState]}
            data-testid={`block-schedule-${block.id}`}
          />
          <Button
            size="small"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            data-testid={`block-up-${block.id}`}
          >
            Up
          </Button>
          <Button
            size="small"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            data-testid={`block-down-${block.id}`}
          >
            Down
          </Button>
          <Button
            size="small"
            onClick={onToggle}
            data-testid={`block-config-toggle-${block.id}`}
          >
            {expanded ? 'Close' : 'Configure'}
          </Button>
          <Button
            size="small"
            color="error"
            onClick={onRemove}
            data-testid={`block-remove-${block.id}`}
          >
            Remove
          </Button>
        </Stack>
        {expanded && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <BlockConfigForm
              block={block}
              onChange={onConfig}
              catalog={catalog}
            />
            {/* The per-block window (8d) is authored ALONGSIDE the strict data editor:
                it is admin / serving metadata, NOT a render-contract field. Empty
                bounds mean an open window; no bounds at all means always visible. */}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Schedule
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Visible from"
                type="datetime-local"
                size="small"
                value={inputValue(window?.visibleFrom ?? null)}
                onChange={(e) => setBound('visibleFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': `block-visible-from-${block.id}` }}
              />
              <TextField
                label="Visible until"
                type="datetime-local"
                size="small"
                value={inputValue(window?.visibleUntil ?? null)}
                onChange={(e) => setBound('visibleUntil', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  'data-testid': `block-visible-until-${block.id}`,
                }}
              />
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const BlockComposer = ({
  blocks,
  onChange,
  catalog,
  schedules,
  onSchedulesChange,
  now,
}: {
  blocks: DraftBlock[];
  onChange: (blocks: DraftBlock[]) => void;
  catalog: Catalog;
  schedules: BlockSchedules;
  onSchedulesChange: (schedules: BlockSchedules) => void;
  now: number;
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const counter = useRef(0);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const addBlock = (type: BlockType) => {
    counter.current += 1;
    const id = `nb-${type}-${counter.current}`;
    onChange([...blocks, { type, id, data: blankBlockData(type) }]);
    setExpanded((prev) => new Set(prev).add(id));
  };

  const move = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= blocks.length) return;
    onChange(arrayMove(blocks, index, to));
  };

  const remove = (id: string) => {
    onChange(blocks.filter((b) => b.id !== id));
    // Drop the removed block's schedule entry so the map never carries an orphan.
    if (schedules[id]) {
      const next = { ...schedules };
      delete next[id];
      onSchedulesChange(next);
    }
  };

  const setWindow = (id: string, window: BlockWindow) => {
    onSchedulesChange({ ...schedules, [id]: window });
  };

  const configure = (id: string, data: Record<string, unknown>) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, data } : b)));
  };

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = blocks.findIndex((b) => b.id === active.id);
    const to = blocks.findIndex((b) => b.id === over.id);
    if (from === -1 || to === -1) return;
    onChange(arrayMove(blocks, from, to));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add a block
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        {BLOCK_TYPES.map((type) => (
          <Button
            key={type}
            size="small"
            variant="outlined"
            onClick={() => addBlock(type)}
            data-testid={`palette-${type}`}
          >
            {BLOCK_TYPE_LABEL[type]}
          </Button>
        ))}
      </Stack>

      {blocks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No blocks yet. Add one from the palette above.
        </Typography>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={blocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <Box data-testid="composer-blocks">
              {blocks.map((block, index) => (
                <SortableBlockCard
                  key={block.id}
                  block={block}
                  index={index}
                  total={blocks.length}
                  expanded={expanded.has(block.id)}
                  onToggle={() => toggle(block.id)}
                  onMove={(dir) => move(index, dir)}
                  onRemove={() => remove(block.id)}
                  onConfig={(data) => configure(block.id, data)}
                  catalog={catalog}
                  window={schedules[block.id]}
                  onWindow={(window) => setWindow(block.id, window)}
                  now={now}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      )}
    </Box>
  );
};
